/**
 * Preis- und Zahlplan-Konfiguration für die Self-Service-Checkouts
 * von Ausbildungs-Bundles (interne Buchung über `/ausbildung/<slug>/kaufen`).
 *
 * Gepflegt: Musik und Sprache, Kita-Gitarrenkurs, Fachkraft für Musik. Muss
 * mit `PRICING` in `supabase/functions/create-bundle-checkout/index.ts` gleich
 * bleiben (CLAUDE.md, „Ausbildungspreise stehen doppelt“).
 *
 * Modell: Tarif × Zahlart.
 *   Tarif (server-seitig ermittelt, EIN Flag: freigeschaltete Mitgliedschaft ja oder nein):
 *     Musik und Sprache: mitglied | extern
 *     Fachkraft für Musik, Kita-Gitarrenkurs: mitglied | standard, dazu aktion
 *     (Code aus dem Infowebinar, gleicher Preis wie mitglied). Bucht ein Mitglied
 *     eine dieser beiden, ruht sein eigenes Abo 12 Monate (Frage 355).
 *   Zahlart: rechnung (one-time, send_invoice 14d) | raten (6× bzw. 12× SEPA, ohne Aufschlag)
 *
 * Stripe-Account: Live (acct_1DPmQPErIiWtbKLA).
 */

import { TRAINING_BUNDLE_IDS } from "./trainingBundles";
import fachkraftMusikTeamImage from "@/assets/fachkraft-musik-team.jpg";
import gitarreHeroImage from "@/assets/gitarre-hero-kinder-instrumente.jpg";

export type BundleTarif = "mitglied" | "extern" | "standard" | "aktion";
export type BundleZahlart = "rechnung" | "raten";

export interface RechnungPrice {
  stripePriceId: string;
  amountCents: number;
  daysUntilDue: number;
}

export interface RatenPrice {
  stripePriceId: string;            // Stripe recurring price (= baseInstallmentCents)
  baseInstallmentCents: number;
  installmentCount: number;
  /** Rundungsausgleich auf die LETZTE Rate (vom stripe-revenue-webhook angehängt). */
  finalAdjustmentCents: number;
  totalCents: number;
  paymentMethodTypes: ReadonlyArray<"sepa_debit">;
}

export interface AktionPrices {
  rechnung: RechnungPrice;
  raten: RatenPrice;
}

export interface TarifPrices {
  tarif: BundleTarif;
  label: string;
  rechnung: RechnungPrice;
  raten: RatenPrice;
  /**
   * Befristeter Aktionspreis desselben Tarifs (Frühbucher-Aktion).
   *
   * Heißt bewusst nicht `aktion`: Bei Gitarre und Fachkraft Musik ist `aktion`
   * bereits ein eigener Tarif-Schlüssel mit anderer Bedeutung (per Rabattcode
   * freigeschaltet, ohne Ablaufdatum).
   *
   * Ob der Preis greift, entscheidet die Serverzeit in `create-bundle-checkout`
   * anhand von `supabase/functions/_shared/fruehbucher-aktion.ts` — nicht das
   * Frontend. Hier stehen die Beträge nur für die Anzeige.
   */
  aktionspreis?: AktionPrices;
}

export interface BundlePricingConfig {
  bundleId: string;
  slug: string;
  title: string;
  image?: string;
  /**
   * Kurze Eckpunkte, die auf der Kaufseite unter dem Bild stehen.
   * Nur Angaben, die auf der jeweiligen Ausbildungsseite belegt sind.
   */
  highlights?: string[];
  tarife: Partial<Record<BundleTarif, TarifPrices>>;
}

/**
 * Frühbucherpreise (je 50 € unter dem regulären Preis).
 * Gültig nur im Fenster aus `_shared/fruehbucher-aktion.ts`; die Preis-IDs
 * wurden am 22.09.2026 live angelegt (siehe Plan, Abschnitt 3).
 */
const AKTION_MITGLIED: AktionPrices = {
  rechnung: {
    stripePriceId: "price_1UIYCwErIiWtbKLAjpGgQHwS",
    amountCents: 44000,
    daysUntilDue: 14,
  },
  raten: {
    stripePriceId: "price_1UIYDWErIiWtbKLA0hbHsSZU",
    baseInstallmentCents: 7333,
    installmentCount: 6,
    // 5 × 7333 + 1 × (7333 + 2) = 44000
    finalAdjustmentCents: 2,
    totalCents: 44000,
    paymentMethodTypes: ["sepa_debit"],
  },
};

const AKTION_EXTERN: AktionPrices = {
  rechnung: {
    stripePriceId: "price_1UIYByErIiWtbKLA7zGdnLan",
    amountCents: 54000,
    daysUntilDue: 14,
  },
  raten: {
    stripePriceId: "price_1UIYDNErIiWtbKLAlXCsVylG",
    baseInstallmentCents: 9000,
    installmentCount: 6,
    finalAdjustmentCents: 0, // 6 × 9000 = 54000 exakt
    totalCents: 54000,
    paymentMethodTypes: ["sepa_debit"],
  },
};

const FACHKRAFT_MUSIK_SPRACHE: BundlePricingConfig = {
  bundleId: TRAINING_BUNDLE_IDS.FACHKRAFT_MUSIK_SPRACHE,
  slug: "fachkraft-musik-und-sprache",
  title: "Fachkraft für Musik und Sprache im Elementarbereich",
  image: fachkraftMusikTeamImage,
  tarife: {
    mitglied: {
      tarif: "mitglied",
      label: "Aktives MusiKonzept-Mitglied",
      rechnung: {
        // Nur mit aktiver Mitgliedschaft. Die frühere Absolventen-Stufe
        // (490 € ohne Mitgliedschaft) ist am 28.09.2026 entfallen, davor
        // schon die dritte Preisstufe 390 €.
        stripePriceId: "price_1TlTO4ErIiWtbKLAVxw4Vhss",
        amountCents: 49000,
        daysUntilDue: 14,
      },
      raten: {
        stripePriceId: "price_1TlZHuErIiWtbKLApGu9eRsS",
        baseInstallmentCents: 8166,
        installmentCount: 6,
        // 5 × 8166 + 1 × (8166 + 4) = 49000
        finalAdjustmentCents: 4,
        totalCents: 49000,
        paymentMethodTypes: ["sepa_debit"],
      },
      aktionspreis: AKTION_MITGLIED,
    },
    extern: {
      tarif: "extern",
      label: "Externe Teilnehmende",
      rechnung: {
        stripePriceId: "price_1TlZHvErIiWtbKLA6Z4JlpJg", // NEU
        amountCents: 59000,
        daysUntilDue: 14,
      },
      raten: {
        stripePriceId: "price_1TlTO5ErIiWtbKLAULqTDA7E", // reuse
        baseInstallmentCents: 9833,
        installmentCount: 6,
        // 5 × 9833 + 1 × (9833 + 2) = 59000
        finalAdjustmentCents: 2,
        totalCents: 59000,
        paymentMethodTypes: ["sepa_debit"],
      },
      aktionspreis: AKTION_EXTERN,
    },
  },
};

/** 790 €: Mitgliedspreis und Preis mit dem Code aus dem Infowebinar (GITARRE100). */
const GITARRE_AKTION: TarifPrices = {
  tarif: "aktion",
  label: "Webinar-Aktion",
  rechnung: {
    stripePriceId: "price_1TxB2vErIiWtbKLApm6u4VbE",
    amountCents: 79000,
    daysUntilDue: 14,
  },
  raten: {
    stripePriceId: "price_1TxB2vErIiWtbKLAZpqjmsaB",
    baseInstallmentCents: 6583,
    installmentCount: 12,
    // 11 × 6583 + 1 × (6583 + 4) = 79000
    finalAdjustmentCents: 4,
    totalCents: 79000,
    paymentMethodTypes: ["sepa_debit"],
  },
};

const FACHKRAFT_GITARRE: BundlePricingConfig = {
  bundleId: TRAINING_BUNDLE_IDS.FACHKRAFT_GITARRE,
  slug: "fachkraft-gitarre",
  title: "Der große Kita-Gitarrenkurs",
  image: gitarreHeroImage,
  highlights: [
    "Online-Module plus Live-Webinare",
    "6–24 Monate, dein Tempo",
    "Zertifikat nach bestandener Prüfung",
    "Raten ohne Aufschlag",
  ],
  tarife: {
    standard: {
      tarif: "standard",
      label: "Standard",
      rechnung: {
        stripePriceId: "price_1TxB2tErIiWtbKLAjSueKSHc",
        amountCents: 89000,
        daysUntilDue: 14,
      },
      raten: {
        stripePriceId: "price_1TxB2uErIiWtbKLAoLkhiCTE",
        baseInstallmentCents: 7417,
        installmentCount: 12,
        // 11 × 7417 + 1 × (7417 - 4) = 89000
        finalAdjustmentCents: -4,
        totalCents: 89000,
        paymentMethodTypes: ["sepa_debit"],
      },
    },
    // Mitgliedspreis (seit 17.10.2026, Fragen 351 und 354) und Code aus dem Infowebinar: derselbe Stripe-Preis.
    mitglied: { ...GITARRE_AKTION, tarif: "mitglied", label: "Mitglied" },
    aktion: GITARRE_AKTION,
  },
};

/** 860 €: Mitgliedspreis und Preis mit dem Code aus dem Infowebinar (MUSIK100). */
const FACHKRAFT_MUSIK_AKTION: TarifPrices = {
  tarif: "aktion",
  label: "Webinar-Aktion",
  rechnung: {
    stripePriceId: "price_1TxNGQErIiWtbKLAeHzBx8vy",
    amountCents: 86000,
    daysUntilDue: 14,
  },
  raten: {
    stripePriceId: "price_1TxNGQErIiWtbKLA6xrSERga",
    baseInstallmentCents: 7166,
    installmentCount: 12,
    // 11 × 7166 + 1 × (7166 + 8) = 86000
    finalAdjustmentCents: 8,
    totalCents: 86000,
    paymentMethodTypes: ["sepa_debit"],
  },
};

const FACHKRAFT_MUSIK: BundlePricingConfig = {
  bundleId: TRAINING_BUNDLE_IDS.FACHKRAFT_MUSIK,
  slug: "fachkraft-musik",
  title: "Fachkraft für Musik im Elementarbereich",
  image: fachkraftMusikTeamImage,
  tarife: {
    standard: {
      tarif: "standard",
      label: "Standard",
      rechnung: {
        stripePriceId: "price_1TxNGPErIiWtbKLABV65I6RO",
        amountCents: 96000,
        daysUntilDue: 14,
      },
      raten: {
        stripePriceId: "price_1TxNGPErIiWtbKLALcTr46JG",
        baseInstallmentCents: 8000,
        installmentCount: 12,
        finalAdjustmentCents: 0,
        totalCents: 96000,
        paymentMethodTypes: ["sepa_debit"],
      },
    },
    // Mitgliedspreis (seit 17.10.2026, Fragen 351 und 354) und Code aus dem Infowebinar: derselbe Stripe-Preis.
    mitglied: { ...FACHKRAFT_MUSIK_AKTION, tarif: "mitglied", label: "Mitglied" },
    aktion: FACHKRAFT_MUSIK_AKTION,
  },
};

export const TRAINING_BUNDLE_PRICING: ReadonlyArray<BundlePricingConfig> = [
  FACHKRAFT_MUSIK_SPRACHE,
  FACHKRAFT_GITARRE,
  FACHKRAFT_MUSIK,
];

export function getBundlePricingBySlug(slug: string): BundlePricingConfig | null {
  return TRAINING_BUNDLE_PRICING.find((b) => b.slug === slug) ?? null;
}

export function getBundlePricingById(bundleId: string): BundlePricingConfig | null {
  return TRAINING_BUNDLE_PRICING.find((b) => b.bundleId === bundleId) ?? null;
}

export function formatEuro(cents: number): string {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(cents / 100);
}

/**
 * Ende der Frühbucher-Aktion, ausgeschrieben.
 *
 * Kopie von `FRUEHBUCHER_AKTION_ENDE_LABEL` aus
 * `supabase/functions/_shared/fruehbucher-aktion.ts`: Das Frontend soll keinen
 * Deno-Code in sein Bundle ziehen. `src/config/__tests__/trainingBundlePricing.test.ts`
 * nagelt beide aneinander, damit sie nicht auseinanderlaufen.
 */
export const AKTION_ENDE_LABEL = "15. Oktober 2026";

/**
 * Was ein Tarif kostet — Aktionspreis, solange die Aktion laut SERVER läuft.
 *
 * `aktionAktiv` kommt aus `detect_tariff`, nie aus einer Uhr im Browser: Über
 * die Preishoheit entscheidet die Edge Function. Ohne Antwort ist `false`
 * richtig, dann steht der reguläre Preis da — lieber kurz zu teuer als ein
 * Rabatt, den der Checkout hinterher nicht einlöst.
 */
export function anzeigeRechnung(tarif: TarifPrices, aktionAktiv: boolean): RechnungPrice {
  return aktionAktiv && tarif.aktionspreis ? tarif.aktionspreis.rechnung : tarif.rechnung;
}

/** Dasselbe für den Ratenplan. */
export function anzeigeRaten(tarif: TarifPrices, aktionAktiv: boolean): RatenPrice {
  return aktionAktiv && tarif.aktionspreis ? tarif.aktionspreis.raten : tarif.raten;
}

/**
 * Nachlass in Cent, den die Aktion bei diesem Tarif bringt. 0, wenn sie nicht
 * läuft oder für den Tarif kein Aktionspreis hinterlegt ist.
 *
 * Der Betrag wird gerechnet und nicht getextet: Stünde „50 €" als Zeichenkette
 * im Code, wäre die Zahl bei der nächsten Preisänderung falsch, ohne dass es
 * jemandem auffällt.
 */
export function aktionRabattCents(tarif: TarifPrices, aktionAktiv: boolean): number {
  if (!aktionAktiv || !tarif.aktionspreis) return 0;
  return Math.max(0, tarif.rechnung.amountCents - tarif.aktionspreis.rechnung.amountCents);
}
